import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, firstValueFrom, map } from 'rxjs';

import { VAConversation } from './conversation.interface';
import { VANote } from './note.interface';
import { HistoryEntry } from './history.interface';

@Injectable({
  providedIn: 'root',
})
export class ConversationService {
  constructor(private firestore: AngularFirestore) {}

  getConversationById(corpId: string, conversationId: string): Observable<VAConversation> {
    const conversationRef = this.firestore.collection('conversations').doc(corpId).collection('conversations');
    return conversationRef.doc(conversationId).valueChanges() as Observable<VAConversation>;
  }

  async getConversationByIdSnapshot(corpId: string, conversationId: string): Promise<VAConversation> {
    const conversationRef = this.firestore.collection('conversations').doc(corpId).collection('conversations');
    const doc = await firstValueFrom(conversationRef.doc(conversationId).get());
    if (doc.exists) {
      return doc.data() as VAConversation;
    } else {
      throw new Error('Conversation not found');
    }
  }

  getNotesByConversationId(corpId: string, conversationId: string): Observable<VANote[]> {
    const notesRef = this.firestore
      .collection('conversations')
      .doc(corpId)
      .collection('conversations')
      .doc(conversationId)
      .collection('notes', ref => ref.orderBy('createdAt', 'asc'));
    return notesRef.snapshotChanges().pipe(
      map(actions =>
        actions.map(a => {
          const data = a.payload.doc.data() as VANote;
          const id = a.payload.doc.id;
          // @ts-ignore TS2783: id is intentionally overwritten by spread
          return { id, ...data };
        }),
      ),
    );
  }

  getMessagesByConversationId(corpId: string, conversationId: string): Observable<any[]> {
    const messagesRef = this.firestore
      .collection('conversations')
      .doc(corpId)
      .collection('conversations')
      .doc(conversationId)
      .collection('messages', ref => ref.orderBy('createdAt', 'asc'));
    return messagesRef.valueChanges() as Observable<any[]>;
  }

  /**
   * Retrieves the history entries for a conversation, ordered by timestamp in ascending order.
   *
   * History entries track changes to conversation resolution status, including the timestamp
   * of the change, the new resolution value, and optionally the principal (user) who made
   * the change.
   *
   * @param corpId - The corporation ID
   * @param conversationId - The conversation ID
   * @returns Observable that emits an array of history entries
   */
  getHistoryByConversationId(corpId: string, conversationId: string): Observable<HistoryEntry[]> {
    return this.firestore
      .collection(`conversations/${corpId}/conversations/${conversationId}/history`, ref => ref.orderBy('timestamp', 'asc'))
      .valueChanges() as Observable<HistoryEntry[]>;
  }
}
